import React from "react";
import { BarChart3, Layers, Lock, ShieldCheck, Users } from "lucide-react";

const JOURNEY_STEPS = ["Discover", "Check Availability", "Book", "Pay", "Manage", "Experience"];

export function PlatformJourney() {
  return (
    <section className="relative overflow-hidden bg-[#012d6e] py-20">
      <div className="pointer-events-none absolute left-1/4 top-0 h-[300px] w-[500px] rounded-full bg-[#017bef]/[0.15] blur-[64px]" />

      <div className="relative mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <p className="text-center text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-white/50">
          The Platform Journey
        </p>
        <h2 className="pt-[12px] text-center text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-white sm:text-[48px] sm:leading-[48px]">
          From Discovery to Experience
        </h2>

        <div className="grid grid-cols-2 gap-[16px] pt-14 sm:grid-cols-3 lg:grid-cols-6">
          {JOURNEY_STEPS.map((step, index) => (
            <div key={step} className="relative flex flex-col items-center">
              {/* Dashed link to the next step (desktop only) */}
              {index < JOURNEY_STEPS.length - 1 && (
                <div className="absolute left-[calc(50%+20px)] top-[20px] hidden w-[calc(100%-40px+16px)] border-t-2 border-dashed border-white/[0.15] lg:block" />
              )}
              <span className="flex size-10 items-center justify-center rounded-full bg-[#017bef] text-[12px] font-bold leading-4 text-white drop-shadow-[0_2px_8px_rgba(1,123,239,0.4)]">
                {String(index + 1).padStart(2, "0")}
              </span>
              <p className="pt-[16px] text-center text-[14px] font-bold leading-[19.25px] text-white">{step}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Relative bar heights from the design's illustrative chart
const PERFORMANCE_BARS = [62, 78, 50, 90, 67, 84, 56, 95];

export function PlatformPartners() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <p className="text-center text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">
          For Partners
        </p>
        <h2 className="pt-[12px] text-center text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
          Built for the People Who Provide the Space
        </h2>

        <div className="grid grid-cols-1 gap-8 pt-14 lg:grid-cols-2">
          <div>
            <p className="text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-[#017bef]">Feature 07</p>
            <h3 className="pt-[12px] text-[20px] font-bold leading-7 text-[#0a0a0a]">Partner Dashboard</h3>
            <p className="pt-2 text-[14px] leading-[22.75px] text-[#717182]">
              Manage your spaces, bookings and availability from one place.
            </p>

            {/* Illustration of the partner dashboard */}
            <div aria-hidden className="mt-6 rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-[#f8fbff] p-6">
              <div className="flex items-center justify-between">
                <p className="text-[14px] font-bold leading-5 text-[#0a0a0a]">Partner Dashboard</p>
                <span className="text-[12px] leading-4 text-[#717182]">Partner view</span>
              </div>
              <div className="grid grid-cols-3 gap-[12px] pt-[20px]">
                {[
                  { label: "Bookings", color: "#017bef" },
                  { label: "Availability", color: "#0062c0" },
                  { label: "Your Space", color: "#012d6e" },
                ].map(tile => (
                  <div
                    key={tile.label}
                    className="flex flex-col items-center rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white p-[16px]"
                  >
                    <span className="size-8 rounded-[14px] opacity-15" style={{ backgroundColor: tile.color }} />
                    <span className="pt-2 text-[10px] font-bold leading-[15px] text-[#0a0a0a]">{tile.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-[20px] overflow-hidden rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white">
                {["Upcoming booking", "Availability update", "Space update"].map(row => (
                  <div
                    key={row}
                    className="flex items-center justify-between border-b-[1px] border-solid border-[#f5f8ff] px-[16px] py-[12px] last:border-b-0"
                  >
                    <span className="text-[12px] leading-4 text-[#717182]">{row}</span>
                    <span className="size-2 rounded-full bg-[#017bef]/25" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-[#017bef]">Feature 08</p>
            <h3 className="pt-[12px] text-[20px] font-bold leading-7 text-[#0a0a0a]">
              See How Your Space Is Performing
            </h3>
            <p className="pt-2 text-[14px] leading-[22.75px] text-[#717182]">
              Reporting and performance analytics for partners — understand how your space is performing.
            </p>

            <div aria-hidden className="mt-6 rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-[#f8fbff] p-6">
              <div className="flex items-center justify-between">
                <p className="text-[14px] font-bold leading-5 text-[#0a0a0a]">Performance</p>
                <BarChart3 className="size-4 text-[#017bef]" />
              </div>
              <div className="flex h-[112px] items-end gap-2 pt-[20px]">
                {PERFORMANCE_BARS.map((height, index) => (
                  <span
                    key={index}
                    className="flex-1 rounded-t-[10px] bg-gradient-to-t from-[#017bef] to-[#60a5fa] opacity-70"
                    style={{ height: `${height}%` }}
                  />
                ))}
              </div>
              <div className="mt-[16px] flex flex-wrap gap-[16px] border-t-[1px] border-solid border-[#e5e7eb] pt-[16px]">
                {["Performance", "Reporting", "Analytics"].map(legend => (
                  <span key={legend} className="flex items-center gap-1.5 text-[10px] leading-[15px] text-[#717182]">
                    <span className="size-2 rounded-full bg-[#017bef]" />
                    {legend}
                  </span>
                ))}
              </div>
              <p className="pt-[12px] text-center text-[10px] leading-[15px] text-[#9ca3af]">
                Illustrative only — no data fabricated
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const SECURITY_POINTS = [
  { title: "Role-based access", description: "Control who can access and manage each part of the platform." },
  { title: "Enterprise security", description: "Built to meet the needs of enterprise users and organisations." },
  {
    title: "Secure for everyone",
    description: "From individual users to organisations, the platform is built to be trusted.",
  },
];

const ACCESS_ROLES = [
  { role: "User", color: "#017bef", permissions: ["Discover", "Book", "Manage"] },
  { role: "Partner", color: "#0062c0", permissions: ["List", "Manage space", "View performance"] },
  { role: "Organisation", color: "#012d6e", permissions: ["Team access", "Enterprise controls"] },
];

export function PlatformSecurity() {
  return (
    <section className="bg-[#f5f8ff] py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 px-[16px] sm:px-8 lg:grid-cols-2">
        <div>
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">Feature 09</p>
          <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
            Built for Secure Access
          </h2>
          <p className="pt-[16px] text-[18px] leading-[29.25px] text-[#717182]">
            Role-based access and enterprise security — the platform is built to be used with confidence at every level.
          </p>
          <ul className="space-y-[16px] pt-8">
            {SECURITY_POINTS.map(point => (
              <li key={point.title} className="flex items-start gap-[16px]">
                <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-[14px] bg-[#017bef]/[0.08]">
                  <Lock className="size-[15px] text-[#017bef]" />
                </span>
                <div>
                  <p className="text-[14px] font-bold leading-5 text-[#0a0a0a]">{point.title}</p>
                  <p className="pt-0.5 text-[14px] leading-5 text-[#717182]">{point.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Illustration of role-based access */}
        <div
          aria-hidden
          className="rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-white p-7 drop-shadow-[0_4px_12px_rgba(0,0,0,0.05)]"
        >
          <div className="flex items-center gap-[12px]">
            <span className="flex size-9 items-center justify-center rounded-[14px] bg-[#017bef]/[0.08]">
              <ShieldCheck className="size-4 text-[#017bef]" />
            </span>
            <p className="text-[14px] font-bold leading-5 text-[#0a0a0a]">Access Control</p>
          </div>

          <div className="space-y-[12px] pt-6">
            {ACCESS_ROLES.map(entry => (
              <div
                key={entry.role}
                className="rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-[#f8fbff] px-[20px] py-[16px]"
              >
                <span className="flex items-center gap-2">
                  <span className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className="text-[14px] font-semibold leading-5 text-[#0a0a0a]">{entry.role}</span>
                </span>
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {entry.permissions.map(permission => (
                    <span
                      key={permission}
                      className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold leading-[15px] text-white"
                      style={{ backgroundColor: entry.color }}
                    >
                      {permission}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const USER_ACTIONS = [
  { pill: "Discover", description: "Find workspaces, stays and experiences" },
  { pill: "Book", description: "Check availability and confirm bookings" },
  { pill: "Manage", description: "Keep all reservations in one place" },
];

const PARTNER_ACTIONS = [
  { pill: "List", description: "Add your space or venue to SPACIA" },
  { pill: "Manage", description: "Control availability and bookings" },
  { pill: "Monitor", description: "Track performance and reporting" },
  { pill: "Grow", description: "Reach SPACIA's growing user base" },
];

export function PlatformEcosystem() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <p className="text-center text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">
          Ecosystem
        </p>
        <h2 className="pt-[12px] text-center text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
          One Platform. Two Sides of the Experience.
        </h2>

        <div className="grid grid-cols-1 gap-6 pt-14 lg:grid-cols-2">
          <div className="rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-[#f8fbff] p-8">
            <div className="flex items-center gap-[12px]">
              <span className="flex size-10 items-center justify-center rounded-2xl bg-[#017bef]">
                <Users className="size-[18px] text-white" />
              </span>
              <p className="text-[18px] font-bold leading-7 text-[#0a0a0a]">Users</p>
            </div>
            <p className="pt-6 text-[14px] leading-[22.75px] text-[#717182]">
              Users can discover, book and manage their Work, Stay and Play experiences through one platform.
            </p>
            <div className="space-y-[12px] pt-6">
              {USER_ACTIONS.map(action => (
                <div
                  key={action.pill}
                  className="flex items-center gap-[16px] rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white px-[20px] py-3.5"
                >
                  <span className="shrink-0 rounded-full bg-[#017bef] px-[12px] py-1 text-[10px] font-bold leading-[15px] tracking-[1px] text-white">
                    {action.pill}
                  </span>
                  <span className="text-[14px] leading-5 text-[#717182]">{action.description}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl bg-[#012d6e] p-8">
            <div className="pointer-events-none absolute right-0 top-0 size-48 rounded-full bg-[#017bef]/20 blur-[64px]" />
            <div className="relative">
              <div className="flex items-center gap-[12px]">
                <span className="flex size-10 items-center justify-center rounded-2xl border-[1px] border-solid border-white/[0.15] bg-white/10">
                  <Layers className="size-[18px] text-white" />
                </span>
                <p className="text-[18px] font-bold leading-7 text-white">Partners</p>
              </div>
              <p className="pt-6 text-[14px] leading-[22.75px] text-white/60">
                Partners can list, manage and understand the performance of their spaces and venues.
              </p>
              <div className="space-y-[12px] pt-6">
                {PARTNER_ACTIONS.map(action => (
                  <div
                    key={action.pill}
                    className="flex items-center gap-[16px] rounded-2xl border-[1px] border-solid border-white/10 bg-white/[0.06] px-[20px] py-3.5"
                  >
                    <span className="shrink-0 rounded-full bg-[#017bef] px-[12px] py-1 text-[10px] font-bold leading-[15px] tracking-[1px] text-white">
                      {action.pill}
                    </span>
                    <span className="text-[14px] leading-5 text-white/60">{action.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
